import React, { useState, useEffect, useRef } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Program, DonationFormData } from '../types';
import { LockIcon, GiftIcon, InfoIcon } from './Icons';
import { createAnonymousPaymentIntent, confirmDonation, getStripeFee } from '../services/api';

interface DonationFormProps {
  program: Program;
  onSuccess: (receiptNumber: string, amount: number) => void;
  onError: (message: string) => void;
  fundRaiseId?: string;
}

const SUGGESTED_AMOUNTS = [10, 25, 50, 100, 250, 500];
const GFTL_OPTIONS = [0, 1, 2, 3, 5];
const REASONS_FOR_DONATION = ['Zakat', 'General Charity', 'Sadaqa'];

const DonationForm: React.FC<DonationFormProps> = ({ program, onSuccess, onError, fundRaiseId }) => {
  const stripe = useStripe();
  const elements = useElements();
  
  // Form state
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [donationReason, setDonationReason] = useState<string>('General Charity');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [gftlContribution, setGftlContribution] = useState<number>(0);
  const [gftlCustom, setGftlCustom] = useState<string>('');
  const [isGiftAid, setIsGiftAid] = useState(false);
  const [houseNo, setHouseNo] = useState('');
  const [postCode, setPostCode] = useState('');
  const [anonymousDonation, setAnonymousDonation] = useState(false);
  const [donationNote, setDonationNote] = useState('');
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [stripeFee, setStripeFee] = useState<number>(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Refs for scroll-to-error
  const amountRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLDivElement>(null);
  const houseNoRef = useRef<HTMLDivElement>(null);
  const postCodeRef = useRef<HTMLDivElement>(null);
  
  // Get actual donation amount
  const donationAmount = selectedAmount || (customAmount ? parseFloat(customAmount) : 0);
  
  // Calculate total amount
  const totalAmount = donationAmount + gftlContribution + stripeFee;
  
  // Gift aid amount (25% of donation)
  const giftAidAmount = isGiftAid ? donationAmount * 0.25 : 0;

  // Fetch stripe fee when amount changes
  useEffect(() => {
    const fetchStripeFee = async () => {
      if (donationAmount > 0 && program?._id) {
        try {
          const feeData = await getStripeFee(donationAmount, gftlContribution, program._id);
          setStripeFee(feeData.stripeFee);
        } catch (err) {
          // Use default calculation if API fails
          setStripeFee(parseFloat(((donationAmount + gftlContribution) * 0.022 + 0.30).toFixed(2)));
        }
      } else {
        setStripeFee(0);
      }
    };
    
    const debounceTimer = setTimeout(fetchStripeFee, 300);
    return () => clearTimeout(debounceTimer);
  }, [donationAmount, gftlContribution, program?._id]);

  // Keep gftlContribution in sync with custom input
  useEffect(() => {
    if (gftlCustom && gftlCustom.trim() !== '') {
      const val = parseFloat(gftlCustom);
      if (!Number.isNaN(val)) setGftlContribution(val);
    }
  }, [gftlCustom]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!donationAmount || donationAmount < 1) {
      newErrors.amount = 'Please enter a valid donation amount (minimum £1)';
    }
    
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (isGiftAid) {
      if (!houseNo.trim()) {
        newErrors.houseNo = 'House number is required for Gift Aid';
      }
      if (!postCode.trim()) {
        newErrors.postCode = 'Post code is required for Gift Aid';
      }
    }
    
    setErrors(newErrors);
    
    // Scroll to first error field
    if (Object.keys(newErrors).length > 0) {
      const errorFieldRefs: Record<string, React.RefObject<HTMLDivElement>> = {
        amount: amountRef,
        email: emailRef,
        name: nameRef,
        houseNo: houseNoRef,
        postCode: postCodeRef,
      };
      
      const firstErrorKey = Object.keys(newErrors)[0];
      const firstErrorRef = errorFieldRefs[firstErrorKey];
      if (firstErrorRef?.current) {
        firstErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      onError('Payment system not ready. Please try again.');
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Create payment intent
      const donationData: DonationFormData = {
        email: email.trim() || undefined,
        name,
        amount: donationAmount,
        donationToGFTL: gftlContribution,
        donationReason,
        address: address.trim() || undefined,
        city: city.trim() || undefined,
        country: country.trim() || undefined,
        isGiftAid,
        houseNo: isGiftAid ? houseNo : undefined,
        postCode: isGiftAid ? postCode : undefined,
        anonymousDonation,
        donationNote: donationNote || undefined,
      };
      
      const paymentIntent = await createAnonymousPaymentIntent(
        program._id,
        donationData,
        totalAmount,
        // pass fundraiser id when present so backend can associate the donation
        fundRaiseId || undefined
      );

      // Stripe payment flow
      const cardElement = elements?.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Confirm payment with Stripe
      const { error, paymentIntent: confirmedIntent } = await stripe!.confirmCardPayment(
        paymentIntent.clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name,
              email: email.trim() || undefined,
            },
          },
        }
      );
      
      if (error) {
        throw new Error(error.message || 'Payment failed');
      }
      
      if (confirmedIntent?.status === 'succeeded') {
        // Confirm and persist donation state in backend before showing success to user.
        if (!paymentIntent.donationId) {
          throw new Error('Payment succeeded but donation reference is missing');
        }
        const confirmation = await confirmDonation(confirmedIntent.id, paymentIntent.donationId);
        if (!confirmation?.success) {
          throw new Error('Payment succeeded but donation confirmation failed');
        }
        onSuccess(confirmation.receiptNumber, totalAmount);
      } else {
        throw new Error('Payment was not completed');
      }
      
    } catch (err: any) {
      onError(err.message || 'An error occurred while processing your donation');
    } finally {
      setIsLoading(false);
    }
  };

  // Format currency for display
  const formatCurrency = (amount: number): string => {
    return `£${amount.toFixed(2)}`;
  };

  // Handle amount selection
  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
    setErrors({ ...errors, amount: '' });
  };

  // Handle custom amount change
  const handleCustomAmountChange = (value: string) => {
    // Only allow numbers and decimal point
    const cleanValue = value.replace(/[^0-9.]/g, '');
    setCustomAmount(cleanValue);
    setSelectedAmount(null);
    setErrors({ ...errors, amount: '' });
  };

  return (
    <div className="donation-form-card">
      <div className="donation-form__header">
        <h2 className="donation-form__title">Make a Donation</h2>
        <p className="donation-form__subtitle">Your generosity creates real impact</p>
      </div>
      
      <form onSubmit={handleSubmit}>
        {/* Amount Selection */}
        <div className="form-group" ref={amountRef}>
          <label className="form-label">Select Amount</label>
          <div className="amount-grid">
            {SUGGESTED_AMOUNTS.map((amount) => (
              <button
                key={amount}
                type="button"
                className={`amount-option ${selectedAmount === amount ? 'amount-option--selected' : ''}`}
                onClick={() => handleAmountSelect(amount)}
              >
                £{amount}
              </button>
            ))}
          </div>
          
          {/* Custom Amount */}
          <div className="custom-amount-wrapper">
            <span className="currency-symbol">£</span>
            <input
              type="text"
              className={`form-input ${errors.amount ? 'form-input--error' : ''}`}
              placeholder="Or enter custom amount"
              value={customAmount}
              onChange={(e) => handleCustomAmountChange(e.target.value)}
            />
          </div>
          {errors.amount && <span className="form-error">{errors.amount}</span>}
        </div>
        
        {/* Email */}
        <div className="form-group" ref={emailRef}>
          <label className="form-label">Email Address *</label>
          <input
            type="email"
            className={`form-input ${errors.email ? 'form-input--error' : ''}`}
            placeholder="your@email.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors({ ...errors, email: '' });
            }}
            required
          />
          {errors.email && <span className="form-error">{errors.email}</span>}
        </div>
        
        {/* Name */}
        <div className="form-group" ref={nameRef}>
          <label className="form-label">Full Name *</label>
          <input
            type="text"
            className={`form-input ${errors.name ? 'form-input--error' : ''}`}
            placeholder="Your full name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setErrors({ ...errors, name: '' });
            }}
          />
          {errors.name && <span className="form-error">{errors.name}</span>}
        </div>
        
        {/* Donation Reason */}
        <div className="form-group">
          <label className="form-label">Reason for Donation</label>
          <select
            className="form-input form-select"
            value={donationReason}
            onChange={(e) => setDonationReason(e.target.value)}
          >
            {REASONS_FOR_DONATION.map((reason) => (
              <option key={reason} value={reason}>
                {reason}
              </option>
            ))}
          </select>
        </div>
        
        {/* Address */}
        <div className="form-group">
          <label className="form-label">Address</label>
          <input
            type="text"
            className="form-input"
            placeholder="Street address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>
        
        {/* City and Country */}
        <div style={{ display: 'flex', gap: '1rem' }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">City</label>
            <input
              type="text"
              className="form-input"
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Country</label>
            <input
              type="text"
              className="form-input"
              placeholder="Country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            />
          </div>
        </div>
        
        {/* GFTL Contribution */}
        <div className="gftl-contribution">
          <div className="gftl-contribution__title">
            Support Giving For The Living
          </div>
          <p className="gftl-contribution__description">
            Help us cover platform costs and reach more donors
          </p>
          <div className="gftl-contribution__options">
            {GFTL_OPTIONS.map((amount) => (
              <button
                key={amount}
                type="button"
                className={`gftl-option ${gftlContribution === amount ? 'gftl-option--selected' : ''}`}
                onClick={() => {
                  setGftlContribution(amount);
                  setGftlCustom('');
                }}
              >
                £{amount}
              </button>
            ))}
            {/* Custom GFTL amount */}
            <div className="gftl-custom-wrapper">
              <span className="currency-symbol">£</span>
              <input
                type="text"
                className={`form-input gftl-custom-input`}
                placeholder="Or enter custom amount"
                value={gftlCustom}
                onChange={(e) => {
                  const cleanValue = e.target.value.replace(/[^0-9.]/g, '');
                  setGftlCustom(cleanValue);
                }}
                onBlur={() => {
                  if (gftlCustom && gftlCustom.trim() !== '') {
                    const val = parseFloat(gftlCustom);
                    if (!Number.isNaN(val)) {
                      // Normalize to 2 decimals
                      setGftlCustom(val.toFixed(2));
                    }
                  }
                }}
                onFocus={() => setSelectedAmount(null)}
              />
            </div>
          </div>
        </div>
        
        {/* Gift Aid Section */}
        <div className="gift-aid-section">
          <div className="gift-aid-header">
            <GiftIcon className="gift-aid-icon" />
            <span className="gift-aid-title">Gift Aid</span>
          </div>
          <p className="gift-aid-description">
            UK taxpayers can boost their donation by 25% at no extra cost
          </p>
          
          <div className="checkbox-group">
            <input
              type="checkbox"
              id="giftAid"
              className="checkbox-input"
              checked={isGiftAid}
              onChange={(e) => setIsGiftAid(e.target.checked)}
            />
            <label htmlFor="giftAid" className="checkbox-label">
              Yes, I am a UK taxpayer and want to Gift Aid my donation
            </label>
          </div>
          
          {isGiftAid && (
            <>
              <div className="gift-aid-amount">
                Your donation worth: {formatCurrency(donationAmount + giftAidAmount)}
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <div style={{ flex: 1 }} ref={houseNoRef}>
                  <input
                    type="text"
                    className={`form-input ${errors.houseNo ? 'form-input--error' : ''}`}
                    placeholder="House No."
                    value={houseNo}
                    onChange={(e) => setHouseNo(e.target.value)}
                  />
                  {errors.houseNo && <span className="form-error">{errors.houseNo}</span>}
                </div>
                <div style={{ flex: 1 }} ref={postCodeRef}>
                  <input
                    type="text"
                    className={`form-input ${errors.postCode ? 'form-input--error' : ''}`}
                    placeholder="Post Code"
                    value={postCode}
                    onChange={(e) => setPostCode(e.target.value)}
                  />
                  {errors.postCode && <span className="form-error">{errors.postCode}</span>}
                </div>
              </div>
            </>
          )}
        </div>
        
          {/* Anonymous Donation */}
          <div className="form-group">
              <div className="checkbox-group checkbox-group--indented" style={{ marginBottom: 0 }}>
                  <input
                      type="checkbox"
                      id="anonymous"
                      className="checkbox-input"
                      checked={anonymousDonation}
                      onChange={(e) => setAnonymousDonation(e.target.checked)}
                  />
                  <label htmlFor="anonymous" className="checkbox-label">
                      Make my donation anonymous
                  </label>
              </div>
          </div>
        
        {/* Donation Note */}
        <div className="form-group">
          <label className="form-label">
            Leave a note (optional)
            <span style={{ marginLeft: '4px', verticalAlign: 'middle', display: 'inline-flex' }}>
              <InfoIcon />
            </span>
          </label>
          <textarea
            className="form-input"
            rows={3}
            placeholder="Share a message with the charity..."
            value={donationNote}
            onChange={(e) => setDonationNote(e.target.value)}
            style={{ resize: 'vertical', minHeight: '80px' }}
          />
        </div>
        
        {/* Donation Summary */}
        <div className="donation-summary">
          <div className="donation-summary__title">Donation Summary</div>
          <div className="donation-summary__row">
            <span className="donation-summary__label">Donation Amount</span>
            <span className="donation-summary__value">{formatCurrency(donationAmount)}</span>
          </div>
          <div className="donation-summary__row">
            <span className="donation-summary__label">GFTL Support</span>
            <span className="donation-summary__value">{formatCurrency(gftlContribution)}</span>
          </div>
          <div className="donation-summary__row">
            <span className="donation-summary__label">Processing Fee</span>
            <span className="donation-summary__value">{formatCurrency(stripeFee)}</span>
          </div>
          {isGiftAid && (
            <div className="donation-summary__row">
              <span className="donation-summary__label">Gift Aid (25%)</span>
              <span className="donation-summary__value" style={{ color: '#28A745' }}>
                +{formatCurrency(giftAidAmount)}
              </span>
            </div>
          )}
          <div className="donation-summary__row donation-summary__row--total">
            <span className="donation-summary__label">Total Charge</span>
            <span className="donation-summary__value">{formatCurrency(totalAmount)}</span>
          </div>
        </div>
        
        {/* Card Element */}
        <div className="form-group">
            <label className="form-label">Card Details</label>
            <div className="stripe-element">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#212529',
                      '::placeholder': {
                        color: '#6C757D',
                      },
                    },
                    invalid: {
                      color: '#DC3545',
                    },
                  },
                  hidePostalCode: true,
                }}
              />
            </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="donate-button"
          disabled={isLoading || !stripe || donationAmount < 1}
        >
          {isLoading ? (
            <>
              <span className="donate-button__spinner" />
              Processing...
            </>
          ) : (
            <>
              Donate {formatCurrency(totalAmount)}
            </>
          )}
        </button>
        
        {/* Security Note */}
        <div className="security-note">
          <LockIcon />
          <span>Secured by Stripe. Your payment information is encrypted.</span>
        </div>
      </form>
    </div>
  );
};

export default DonationForm;
